/**
 * @module RecruitingBewerberUnified
 * @description Unified Bewerber-Liste — Fest + Flex in einer Ansicht mit Typ-Filter,
 *   Listen-View, Create-Modal.
 * @route /recruiting/bewerber
 * @since v2.246.0
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const fj = (url, body, method = 'POST') => f(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';
const daysSince = (d) => d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)) : null;
const TYP_LABELS = { vollzeit: 'Vollzeit', teilzeit: 'Teilzeit', lehrling: 'Lehrling', praktikum: 'Praktikum', flex: 'Flex', geringfuegig: 'Geringfügig' };
const QUELLEN = ['manuell', 'karriereseite', 'indeed', 'linkedin', 'empfehlung', 'ams', 'initiativ', 'flex_uebernahme', 'jobs_landing'];

// #958: Status ist die Bewerber-Stufe (Spec aus Detail-View FlexBewerberDetail.jsx STATUS_OPTIONS).
// Reihenfolge bestimmt Pill-Anzeige + Default-Sort (aktive oben, abgelehnt/inaktiv unten).
const STATUS_DEF = [
  { key: 'aktiv',      color: '#10B981', bg: '#D1FAE5', sort: 1 },
  { key: 'zugesagt',   color: '#22D3EE', bg: '#CFFAFE', sort: 2 },
  { key: 'eingeladen', color: '#22D3EE', bg: '#CFFAFE', sort: 3 },
  { key: 'in_review',  color: '#F59E0B', bg: '#FEF3C7', sort: 4 },
  { key: 'beworben',   color: '#338edc', bg: '#EBF4FD', sort: 5 },
  { key: 'abgelehnt',  color: '#EF4444', bg: '#FEE2E2', sort: 6 },
  { key: 'inaktiv',    color: '#9CA3AF', bg: '#F3F4F6', sort: 7 },
];
const STATUS_MAP = Object.fromEntries(STATUS_DEF.map(s => [s.key, s]));
// Aliase für Robustheit gegen DEV-Test-Daten (`in_pruefung` → `in_review`, `eingegangen` → `beworben`)
const STATUS_ALIASES = { in_pruefung: 'in_review', eingegangen: 'beworben' };
const canonicalStatus = (s) => {
  if (!s) return null;
  const stripped = s.replace(/^flex_/, '');
  return STATUS_ALIASES[stripped] || stripped;
};
const STATUS_FALLBACK = { color: '#6B7280', bg: '#F3F4F6', sort: 99 };

const IconStar = ({ filled }) => <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;

export default function RecruitingBewerberUnified() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [bewerber, setBewerber] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Filters
  const [typFilter, setTypFilter] = useState(searchParams.get('typ') || '');
  const [filter, setFilter] = useState({});
  const [search, setSearch] = useState('');
  const [stellen, setStellen] = useState([]);

  // #959-fix: Default-Sort = Datum DESC (neueste oben). User klickt Header zum Umschalten.
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const toggleSort = (key, defaultDir = 'asc') => {
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: defaultDir });
  };

  // Auto-open create modal via URL param (from Dashboard quick-action)
  // Auto-filter by stelle from URL param (from Stellen page bewerber count link)
  useEffect(() => {
    if (searchParams.get('create') === '1') setShowCreate(true);
    const stelleParam = searchParams.get('stelle');
    if (stelleParam) setFilter(f => ({ ...f, stelle: stelleParam }));
  }, [searchParams]);

  // Load stellen (#959: pipeline-fetch entfernt — Status = Single Source of Truth)
  useEffect(() => {
    f('/api/recruiting/stellen')
      .then(r => r.ok ? r.json() : [])
      .then(s => setStellen(Array.isArray(s) ? s : []))
      .catch(() => {});
  }, []);

  // Load bewerber (fest)
  // #958: Server-Filter `?stufe=` (Pipeline) entfernt — Status-Filter erfolgt clientseitig auf b.status.
  const loadFest = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter.stelle) params.set('stelle', filter.stelle);
    if (filter.quelle) params.set('quelle', filter.quelle);
    if (search) params.set('search', search);
    params.set('limit', '200');
    try {
      const res = await f(`/api/recruiting/bewerber?${params}`);
      if (res.ok) {
        const data = await res.json();
        setBewerber(data.bewerber || []);
      }
    } catch { /* */ }
  }, [filter.stelle, filter.quelle, search]);

  useEffect(() => {
    setLoading(true);
    loadFest().finally(() => setLoading(false));
  }, [loadFest]);

  // Merge bewerber based on filter
  // #958: Single Source of Truth = `bewerber` (alle, fest+flex). Filter clientseitig auf b.status.
  // #959-fix: Sortierung user-steuerbar via Header-Klick (`sort.key/dir`). Default = Datum DESC.
  const allBewerber = (() => {
    let list = bewerber.map(b => ({
      ...b,
      _typ: b.kategorie === 'flex' ? 'flex' : 'fest',
      _statusKey: canonicalStatus(b.status),
    }));
    if (typFilter === 'fest') list = list.filter(b => b.kategorie !== 'flex');
    if (typFilter === 'flex') list = list.filter(b => b.kategorie === 'flex');
    if (filter.status) list = list.filter(b => b._statusKey === filter.status);

    const cmpKey = (a, b) => {
      switch (sort.key) {
        case 'name': {
          const na = `${a.nachname || ''} ${a.vorname || ''}`.toLowerCase();
          const nb = `${b.nachname || ''} ${b.vorname || ''}`.toLowerCase();
          return na.localeCompare(nb, 'de');
        }
        case 'typ':
          return (a._typ || '').localeCompare(b._typ || '');
        case 'status': {
          const sa = (STATUS_MAP[a._statusKey] || STATUS_FALLBACK).sort;
          const sb = (STATUS_MAP[b._statusKey] || STATUS_FALLBACK).sort;
          return sa - sb;
        }
        case 'position':
          return (a.stelle_titel || a.position || '').localeCompare(b.stelle_titel || b.position || '', 'de');
        case 'quelle':
          return (a.quelle || '').localeCompare(b.quelle || '');
        case 'bewertung': {
          const va = parseFloat(a.bewertung_sterne || a.bewertung_avg || 0);
          const vb = parseFloat(b.bewertung_sterne || b.bewertung_avg || 0);
          return va - vb;
        }
        case 'created_at':
        default: {
          const da = a.created_at ? new Date(a.created_at).getTime() : 0;
          const db = b.created_at ? new Date(b.created_at).getTime() : 0;
          return da - db;
        }
      }
    };
    return list.sort((a, b) => {
      const r = cmpKey(a, b);
      return sort.dir === 'asc' ? r : -r;
    });
  })();

  // #958: Counts aus `bewerber` (kategorie-basiert), unabhängig von Status-Filter.
  const combinedTotal = allBewerber.length;
  const festCount = bewerber.filter(b => b.kategorie !== 'flex').length;
  const flexCount = bewerber.filter(b => b.kategorie === 'flex').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('recruiting.bewerber.title', 'Bewerber')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {combinedTotal} {t('recruiting.bewerber.gesamt', 'Bewerber insgesamt')}
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: '#338edc' }}>
          + {t('recruiting.bewerber.neu', 'Neuer Bewerber')}
        </button>
      </div>

      {/* Typ-Filter Chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: '', label: t('recruiting.bewerber.alle', 'Alle'), count: festCount + flexCount },
          { key: 'fest', label: t('recruiting.bewerber.fest', 'Festanstellung'), count: festCount },
          { key: 'flex', label: t('recruiting.bewerber.flex', 'Flex-Worker'), count: flexCount },
        ].map(chip => (
          <button key={chip.key} onClick={() => setTypFilter(chip.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors"
            style={{
              borderColor: typFilter === chip.key ? '#338edc' : 'var(--border-card, #E5E7EB)',
              background: typFilter === chip.key ? '#EBF4FD' : 'transparent',
              color: typFilter === chip.key ? '#338edc' : 'var(--text-secondary, #4B5563)',
            }}>
            {chip.label} ({chip.count})
          </button>
        ))}

      </div>

      {/* Search + Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t('recruiting.bewerber.suche', 'Name oder E-Mail suchen...')}
          className="w-full max-w-xs px-4 py-2.5 rounded-xl border text-sm"
          style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)' }} />
        {typFilter !== 'flex' && (
          <select value={filter.stelle || ''} onChange={(e) => setFilter(p => ({ ...p, stelle: e.target.value || undefined }))}
            className="px-3 py-2.5 rounded-xl border text-xs"
            style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
            <option value="">{t('recruiting.bewerber.alleStellen', 'Alle Stellen')}</option>
            {stellen.filter(s => s.status === 'aktiv').map(s => <option key={s.id} value={s.id}>{s.titel}</option>)}
          </select>
        )}
        <select value={filter.quelle || ''} onChange={(e) => setFilter(p => ({ ...p, quelle: e.target.value || undefined }))}
          className="px-3 py-2.5 rounded-xl border text-xs"
          style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
          <option value="">{t('recruiting.bewerber.alleQuellen', 'Alle Quellen')}</option>
          {QUELLEN.map(q => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>

      {/* #958: Status pills — einheitlich für alle 3 Tabs (Status = Bewerber-Stufe). */}
      {(() => {
        const scope = typFilter === 'fest'
          ? bewerber.filter(b => b.kategorie !== 'flex')
          : typFilter === 'flex'
            ? bewerber.filter(b => b.kategorie === 'flex')
            : bewerber;
        const counts = {};
        scope.forEach(b => {
          const k = canonicalStatus(b.status);
          if (k) counts[k] = (counts[k] || 0) + 1;
        });
        const totalScope = scope.length;
        return (
          <div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setFilter(p => ({ ...p, status: undefined }))}
                className="px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
                style={{ color: !filter.status ? '#338edc' : 'var(--text-secondary)', borderColor: !filter.status ? '#338edc' : 'var(--border-card)', background: !filter.status ? '#EBF4FD' : 'transparent' }}>
                {t('recruiting.bewerber.alle', 'Alle')} ({totalScope})
              </button>
              {STATUS_DEF.filter(s => (counts[s.key] || 0) > 0 || filter.status === s.key).map(s => (
                <button key={s.key} onClick={() => setFilter(p => ({ ...p, status: filter.status === s.key ? undefined : s.key }))}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
                  style={{
                    background: filter.status === s.key ? s.bg : 'transparent',
                    color: filter.status === s.key ? s.color : 'var(--text-secondary)',
                    borderColor: filter.status === s.key ? s.color : 'var(--border-card)',
                  }}>
                  {t(`flex.bewerber.status.${s.key}`, { defaultValue: s.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) })} ({counts[s.key] || 0})
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── LIST VIEW ── */}
      {(
        <div className="bg-[var(--bg-card,#fff)] rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
          {loading ? (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('common.loading', 'Laden...')}</div>
          ) : allBewerber.length === 0 ? (
            <div className="py-16 text-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-4" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('recruiting.bewerber.keine', 'Keine Bewerber gefunden')}
              </p>
              <p className="text-xs mb-4" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('recruiting.bewerber.keineHinweis', 'Passen Sie die Filter an oder erstellen Sie einen neuen Bewerber.')}
              </p>
              <button onClick={() => setShowCreate(true)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#338edc' }}>
                + {t('recruiting.bewerber.neu', 'Neuer Bewerber')}
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
                  {[
                    { key: 'name',       label: t('recruiting.bewerber.name', 'Name'),         dDir: 'asc'  },
                    { key: 'typ',        label: t('recruiting.bewerber.typ', 'Typ'),           dDir: 'asc'  },
                    { key: 'status',     label: t('recruiting.bewerber.stufe', 'Stufe'),       dDir: 'asc'  },
                    { key: 'position',   label: t('recruiting.bewerber.position', 'Position'), dDir: 'asc'  },
                    { key: 'quelle',     label: t('recruiting.bewerber.quelle', 'Quelle'),     dDir: 'asc'  },
                    { key: 'created_at', label: t('recruiting.bewerber.datum', 'Datum'),       dDir: 'desc' },
                    { key: 'bewertung',  label: t('recruiting.bewerber.bewertung', 'Bewertung'), dDir: 'desc' },
                  ].map(col => {
                    const active = sort.key === col.key;
                    return (
                      <th key={col.key} onClick={() => toggleSort(col.key, col.dDir)}
                        className="text-left px-4 py-3 font-semibold cursor-pointer select-none transition-colors hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                        style={{ color: active ? '#338edc' : 'var(--text-secondary)' }}>
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          <span className="text-[10px] opacity-70" style={{ width: '8px', display: 'inline-block' }}>
                            {active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                          </span>
                        </span>
                      </th>
                    );
                  })}
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {allBewerber.map(b => (
                  <tr key={`${b._typ}-${b.id}`}
                    onClick={() => b._typ === 'flex' ? navigate(`/recruiting/bewerber/${b.id}?typ=flex`) : navigate(`/recruiting/bewerber/${b.id}`)}
                    className="border-t cursor-pointer transition-colors hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                    style={{ borderColor: 'var(--border-table, #F3F4F6)' }}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                      {b.vorname} {b.nachname}
                      {parseInt(b.ungelesen_count) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold align-middle"
                          style={{ background: '#EBF4FD', color: '#338edc' }}
                          title={t('recruiting.bewerber.unreadTooltip', '{n} ungelesene Nachricht(en) — Bewerber-Antwort offen').replace('{n}', b.ungelesen_count)}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                          </svg>
                          {b.ungelesen_count}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                        style={{ background: b._typ === 'flex' ? '#ECFEFF' : '#EBF4FD', color: b._typ === 'flex' ? '#0891B2' : '#338edc' }}>
                        {b._typ === 'flex' ? 'Flex' : 'Fest'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const key = b._statusKey || canonicalStatus(b.status);
                        if (!key) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                        const meta = STATUS_MAP[key] || STATUS_FALLBACK;
                        return (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                            style={{ background: meta.bg, color: meta.color }}>
                            {t(`flex.bewerber.status.${key}`, { defaultValue: key })}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{b.stelle_titel || b.position || '-'}</td>
                    <td className="px-4 py-3 capitalize" style={{ color: 'var(--text-tertiary)' }}>{b.quelle || '-'}</td>
                    <td className="px-4 py-3 tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                      <span>{formatDate(b.created_at)}</span>
                      {daysSince(b.created_at) !== null && (
                        <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-lg font-medium"
                          style={{
                            background: daysSince(b.created_at) > 30 ? '#FEF2F2' : daysSince(b.created_at) > 14 ? '#FEF3C7' : '#F3F4F6',
                            color: daysSince(b.created_at) > 30 ? '#DC2626' : daysSince(b.created_at) > 14 ? '#D97706' : '#6B7280',
                          }}>
                          {daysSince(b.created_at)}{t('recruiting.bewerber.tageInPipeline', 'd')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(b.bewertung_sterne > 0 || b.bewertung_avg > 0) ? (
                        <div className="flex items-center gap-0.5" style={{ color: '#F59E0B' }}>
                          {b.bewertung_sterne > 0
                            ? [1, 2, 3, 4, 5].map(s => <IconStar key={s} filled={s <= b.bewertung_sterne} />)
                            : <span className="text-xs">★ {parseFloat(b.bewertung_avg).toFixed(1)}</span>
                          }
                        </div>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td className="px-2 py-3">
                      {!b.mitarbeiter_id && (
                        <button onClick={e => { e.stopPropagation(); setDeleteTarget(b); }}
                          className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                          style={{ color: '#DC2626' }} title={t('recruiting.bewerber.deleteButton', 'Löschen')}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && <CreateModal t={t} stellen={stellen} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadFest(); }} />}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteTarget(null)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border w-full max-w-md mx-4 p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('recruiting.bewerber.deleteConfirmTitle', 'Bewerber löschen')}
            </h3>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.bewerber.deleteConfirmText', '{name} wirklich löschen? Alle Aktivitäten, Dokumente und Interviews werden ebenfalls gelöscht.').replace('{name}', `${deleteTarget.vorname} ${deleteTarget.nachname}`)}
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-[10px] border text-sm font-medium"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button onClick={async () => {
                try {
                  const res = await fj(`/api/recruiting/bewerber/${deleteTarget.id}`, null, 'DELETE');
                  if (res.ok) { setDeleteTarget(null); loadFest(); }
                  else { const d = await res.json().catch(() => ({})); alert(d.error || 'Fehler'); setDeleteTarget(null); }
                } catch { alert('Netzwerkfehler'); setDeleteTarget(null); }
              }}
                className="px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
                {t('recruiting.bewerber.deleteButton', 'Löschen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create Modal (unified, fest+flex) ───
function CreateModal({ t, stellen, onClose, onCreated }) {
  const [typ, setTyp] = useState('fest');
  const [form, setForm] = useState({ vorname: '', nachname: '', email: '', telefon: '', quelle: 'manuell', notizen: '', stelle_id: '', geburtsdatum: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Escape key closes modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vorname.trim() || !form.nachname.trim()) { setError(t('recruiting.bewerber.pflichtfelder', 'Vorname und Nachname sind Pflichtfelder')); return; }
    setSaving(true);
    try {
      if (typ === 'fest') {
        const body = { ...form, stelle_id: form.stelle_id ? parseInt(form.stelle_id) : null, kategorie: 'fest' };
        const res = await fj('/api/recruiting/bewerber', body);
        if (res.ok) onCreated();
        else { const data = await res.json().catch(() => ({})); setError(data.error || 'Fehler'); }
      } else {
        const body = { vorname: form.vorname, nachname: form.nachname, email: form.email, telefon: form.telefon, quelle: form.quelle, notizen: form.notizen, geburtsdatum: form.geburtsdatum || null, status: 'beworben' };
        const res = await fj('/api/flex/bewerber', body);
        if (res.ok) onCreated();
        else { const data = await res.json().catch(() => ({})); setError(data.error || 'Fehler'); }
      }
    } catch { setError('Verbindungsfehler'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border p-6 w-full max-w-md mx-4" style={{ borderColor: '#E5E7EB' }} onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          {t('recruiting.bewerber.neu', 'Neuer Bewerber')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Typ selection */}
          <div className="flex gap-2">
            {['fest', 'flex'].map(v => (
              <button key={v} type="button" onClick={() => setTyp(v)}
                className="flex-1 py-2 rounded-lg text-sm font-medium border transition-colors"
                style={{
                  borderColor: typ === v ? '#338edc' : '#E5E7EB',
                  background: typ === v ? '#EBF4FD' : 'transparent',
                  color: typ === v ? '#338edc' : 'var(--text-secondary)',
                }}>
                {v === 'fest' ? t('recruiting.bewerber.fest', 'Festanstellung') : t('recruiting.bewerber.flex', 'Flex-Worker')}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={form.vorname} onChange={e => setForm(p => ({ ...p, vorname: e.target.value }))} required
              placeholder={`${t('recruiting.bewerber.vorname', 'Vorname')} *`} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
            <input value={form.nachname} onChange={e => setForm(p => ({ ...p, nachname: e.target.value }))} required
              placeholder={`${t('recruiting.bewerber.nachname', 'Nachname')} *`} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
          </div>
          <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            placeholder={t('recruiting.bewerber.email', 'E-Mail')} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
          <input value={form.telefon} onChange={e => setForm(p => ({ ...p, telefon: e.target.value }))}
            placeholder={t('recruiting.bewerber.telefon', 'Telefon')} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }} />
          {typ === 'fest' && (
            <select value={form.stelle_id} onChange={e => setForm(p => ({ ...p, stelle_id: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }}>
              <option value="">{t('recruiting.bewerber.stelleWaehlen', 'Stelle wählen (optional)')}</option>
              {stellen.filter(s => s.status === 'aktiv').map(s => <option key={s.id} value={s.id}>{s.titel}</option>)}
            </select>
          )}
          <select value={form.quelle} onChange={e => setForm(p => ({ ...p, quelle: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }}>
            {['manuell', 'karriereseite', 'indeed', 'linkedin', 'empfehlung', 'ams', 'initiativ', 'jobs_landing'].map(q => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
          <textarea value={form.notizen} onChange={e => setForm(p => ({ ...p, notizen: e.target.value }))}
            placeholder={t('recruiting.bewerber.notizen', 'Notizen')} rows={2} className="w-full px-3 py-2 rounded-lg border text-sm resize-none" style={{ borderColor: '#E5E7EB' }} />
          {error && <p className="text-xs font-medium" style={{ color: '#EF4444' }}>{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border text-sm font-medium" style={{ borderColor: '#E5E7EB', color: '#4B5563' }}>
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
              {saving ? '...' : t('common.save', 'Speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
